// src/features/stock-ranking/components/Sparkline.tsx
//
// Tiny inline trend line for the Forge Ultra Signal, rendered once per
// visible table row. Deliberately hand-rolled SVG (no Recharts) so the
// table can paint dozens of these without the per-instance overhead of
// a ResponsiveContainer + chart engine — the same dependency-free
// approach used by AllocationDonut and StressTestChart elsewhere in the
// SPA. The colour follows the slope: green when the latest value is at
// or above the first, red when it has fallen.

import type { HistoryPoint } from '../api';

interface SparklineProps {
    series: HistoryPoint[];
    width?: number;
    height?: number;
}

export const Sparkline = ({
    series,
    width = 96,
    height = 28,
}: SparklineProps) => {
    // Need at least two points to draw a line. A single point (or none)
    // renders a flat neutral dash so the cell never looks broken.
    if (!series || series.length < 2) {
        return (
            <svg
                width={width}
                height={height}
                role="img"
                aria-label="Insufficient history"
            >
                <line
                    x1={2}
                    y1={height / 2}
                    x2={width - 2}
                    y2={height / 2}
                    className="stroke-slate-300 dark:stroke-slate-600"
                    strokeWidth={1.5}
                    strokeDasharray="3 3"
                />
            </svg>
        );
    }

    const values = series.map((p) => p.forge_ultra_signal);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1; // avoid /0 on a perfectly flat series.

    const pad = 2;
    const innerW = width - pad * 2;
    const innerH = height - pad * 2;

    // Map each point to SVG space. y is inverted (0 = top) so a rising
    // value moves the line upward as a reader expects.
    const points = values.map((v, i) => {
        const x = pad + (i / (values.length - 1)) * innerW;
        const y = pad + (1 - (v - min) / span) * innerH;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
    });

    const rising = values[values.length - 1] >= values[0];
    const strokeClass = rising
        ? 'stroke-green-600 dark:stroke-emerald-400'
        : 'stroke-red-500 dark:stroke-red-400';

    return (
        <svg
            width={width}
            height={height}
            role="img"
            aria-label={`Signal trend, ${rising ? 'up' : 'down'} over ${series.length} sessions`}
        >
            <polyline
                points={points.join(' ')}
                fill="none"
                className={strokeClass}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
};

export default Sparkline;
